import { useEffect, useState } from "react";
import {
  ArrowRight,
  CheckCircle2,
  ClipboardCheck,
  ShieldCheck,
} from "lucide-react";

import { track } from "@/lib/analytics";
import { machineThreeAPage } from "@/marketing/machine-three-a-catalog";

const questions: Record<string, string[]> = {
  healthcare: [
    "Critical suppliers have a current qualification decision",
    "Mandatory supplier documents have owners and expiry dates",
    "Substitutions require authorised acceptance",
    "Equipment or service completion evidence is retained",
    "Receipt evidence is required before payment-ready status",
  ],
  construction: [
    "Purchases reference a project and budget context",
    "Non-L1 awards retain technical or commercial rationale",
    "Material substitutions require approval",
    "Partial and rejected deliveries preserve outstanding quantities",
    "Service invoices require completion evidence",
  ],
  hospitality: [
    "Department requests retain property and cost-centre context",
    "Rate-contract and off-contract purchases are distinguished",
    "Emergency purchases receive retrospective review",
    "Partial deliveries and substitutions remain attributable",
    "Receipt acceptance exists before payment-ready status",
  ],
  facilities: [
    "Purchases retain a facility and optional work reference",
    "Contract, AMC and off-contract routes are distinguished",
    "Emergency repairs follow a governed exception",
    "Service completion requires requester or technical acceptance",
    "Repeated failures affect supplier evaluation",
  ],
};

const eyebrowForKind: Record<string, string> = {
  solution: "PROCUREMENT CONTROL GUIDANCE",
  standard: "FRAMEWORK GUIDANCE",
  advisor: "ROLE GUIDANCE",
  vertical: "INDUSTRY GUIDANCE",
};

const boundaries: Record<string, string> = {
  healthcare:
    "RAQOZ does not replace HIS, LIS, LIMS, clinical records, medical inventory, biomedical engineering, eQMS, accounting or payment systems.",
  construction:
    "RAQOZ does not replace estimating, BOQ, BIM, project management, contractor measurement, engineering certification, accounting or CostGrid.",
  hospitality:
    "RAQOZ does not replace PMS, POS, recipe costing, hotel inventory, accounting, guest management, payment or logistics systems.",
  facilities:
    "RAQOZ does not replace CMMS, work orders, building systems, field service, leasing, asset ledgers, inventory, accounting or payments.",
};

export function MachineThreeAVerticalPage({ path }: { path: string }) {
  const page = machineThreeAPage(path);
  const [selected, setSelected] = useState<boolean[]>([]);
  const [complete, setComplete] = useState(false);
  useEffect(() => {
    if (!page) return;
    track(
      page.kind === "vertical" ? "vertical_page_viewed" : "control_gap_viewed",
      {
        page: page.path,
        ...(page.kind === "vertical" ? { vertical_slug: page.vertical } : {}),
      },
    );
  }, [page]);
  if (!page) return null;
  const verticalQuestions =
    page.kind === "vertical" ? questions[page.vertical] : [];
  const score = verticalQuestions.length
    ? Math.round(
        (selected.filter(Boolean).length / verticalQuestions.length) * 100,
      )
    : 0;

  return (
    <section className="bg-surface-paper px-5 py-16 text-on-paper md:px-8 md:py-24">
      <div className="mx-auto max-w-6xl">
        <span className="inline-flex rounded-md bg-status-beta-bg px-2.5 py-1 text-[10px] font-semibold tracking-[.1em] text-status-beta-on-light">
          {page.status}
        </span>
        <p className="marketing-eyebrow-light mt-7">
          {eyebrowForKind[page.kind]}
        </p>
        <h1 className="mt-4 max-w-4xl font-serif text-5xl leading-tight md:text-6xl">
          {page.title}
        </h1>
        <p className="mt-6 max-w-3xl text-lg leading-8 text-on-paper-muted">
          {page.description}
        </p>
        <div className="mt-10 flex flex-wrap gap-3">
          <a
            className="marketing-button-primary-light"
            href={page.kind === "vertical" ? "#readiness" : "/founding-circle"}
          >
            {page.kind === "vertical"
              ? "Check your controls"
              : "Request controlled-pilot access"}
            <ArrowRight className="size-4" />
          </a>
          <a
            className="marketing-button-secondary-light"
            href="/tools/procurement-evidence-score"
          >
            Use the evidence score
          </a>
        </div>

        <div className="mt-14 grid gap-5 md:grid-cols-3">
          <Card title="Why allowed">
            Business need, budget context, applicable policy, approval and any
            exception remain linked.
          </Card>
          <Card title="Why this supplier">
            Qualification status, sourcing evidence, evaluation and selection
            rationale remain attributable.
          </Card>
          <Card title="Why payment-ready">
            Receipt or service acceptance, matching evidence and open exceptions
            are evaluated under the active policy.
          </Card>
        </div>

        {page.kind === "vertical" ? (
          <>
            <section
              className="mt-14 rounded-[2rem] border border-line-light bg-surface-card p-7 md:p-10"
              id="readiness"
            >
              <p className="marketing-eyebrow-light">NO-SIGNUP ASSESSMENT</p>
              <h2 className="mt-4 font-serif text-4xl">
                Can you prove one purchase in ten minutes?
              </h2>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-on-paper-muted">
                Select only controls you can evidence today. Do not enter
                supplier names, purchase values, item descriptions, documents or
                private operational information.
              </p>
              <div className="mt-7 grid gap-3">
                {verticalQuestions.map((question, index) => (
                  <label
                    className="flex min-h-12 cursor-pointer items-start gap-3 rounded-xl border border-line-light p-4"
                    key={question}
                  >
                    <input
                      checked={Boolean(selected[index])}
                      className="mt-1 size-4"
                      type="checkbox"
                      onChange={(event) => {
                        const next = [...selected];
                        next[index] = event.target.checked;
                        setSelected(next);
                        setComplete(false);
                        if (!selected.length)
                          track("vertical_assessment_started", {
                            vertical_slug: page.vertical,
                            tool: "purchase_control_readiness",
                          });
                      }}
                    />
                    <span>{question}</span>
                  </label>
                ))}
              </div>
              <button
                className="marketing-button-primary-light mt-6"
                type="button"
                onClick={() => {
                  setComplete(true);
                  track("vertical_assessment_completed", {
                    vertical_slug: page.vertical,
                    tool: "purchase_control_readiness",
                    band:
                      score >= 80
                        ? "strong"
                        : score >= 40
                          ? "developing"
                          : "priority_gaps",
                  });
                }}
              >
                See readiness result
              </button>
              {complete && (
                <div
                  className="mt-6 rounded-2xl bg-teal-wash p-6"
                  role="status"
                >
                  <strong className="text-3xl text-teal-deep">
                    {score}% evidence readiness
                  </strong>
                  <p className="mt-2 text-sm leading-6">
                    {verticalQuestions.length - selected.filter(Boolean).length}{" "}
                    control area(s) still need evidence or an
                    organisation-approved exception. This educational result is
                    not a compliance score, certification or audit opinion.
                  </p>
                  <a
                    className="marketing-text-link-light mt-4"
                    href="/signup"
                    onClick={() =>
                      track("vertical_signup_started", {
                        vertical_slug: page.vertical,
                        source: "readiness_result",
                      })
                    }
                  >
                    Create your first complete Control Passport free{" "}
                    <ArrowRight className="size-4" />
                  </a>
                </div>
              )}
            </section>
            <p className="mt-8 rounded-xl border border-line-light bg-surface-card p-5 text-sm leading-6">
              <strong>System boundary:</strong> {boundaries[page.vertical]}
            </p>
          </>
        ) : (
          <section className="mt-14 rounded-[2rem] bg-surface-ink p-8 text-on-ink-strong md:p-12">
            <ShieldCheck className="size-8 text-teal-bright" />
            <h2 className="mt-5 font-serif text-4xl">
              Evidence support, not assurance.
            </h2>
            <p className="mt-4 max-w-3xl leading-7 text-on-ink">
              RAQOZ helps an organisation create, retain and retrieve evidence
              behind its procurement controls. It does not issue certification,
              audit opinions, tax advice, legal conclusions or a universal
              supplier-safety decision.
            </p>
          </section>
        )}
      </div>
    </section>
  );
}

function Card({ title, children }: { title: string; children: string }) {
  return (
    <article className="rounded-[1.7rem] border border-line-light bg-surface-card p-6">
      <span className="grid size-11 place-items-center rounded-2xl bg-teal-wash text-teal-deep">
        {title === "Why allowed" ? (
          <ClipboardCheck className="size-5" />
        ) : (
          <CheckCircle2 className="size-5" />
        )}
      </span>
      <h2 className="mt-5 font-serif text-2xl">{title}</h2>
      <p className="mt-3 text-sm leading-6 text-on-paper-muted">{children}</p>
    </article>
  );
}
